import React, { useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { getPaletteEntry } from '../../../statusPalette';
import { useNodeDimensions } from '../../hooks/useNodeDimensions';
import { useTabFocalTrigger } from '../../hooks/useTabFocalTrigger';
import { roundAriaLabel } from '../ariaLabels';
import type { LlmRoundData } from '../../types';

// v0.4 Task 7: rich llm_round card.
//
//   Kind identity: ⚙ glyph (⚡ for synthesis rounds).
//   Status identity: runState → palette key (running → violet,
//     error → rose, completed → emerald-ok).
//
// wrapperRef lives here so Task 10 can wire useNodeDimensions.

function shortenModel(modelId?: string | null): string | null {
  if (!modelId) return null;
  const tail = modelId.split('/').pop() ?? modelId;
  return tail.replace(/-\d{8}$/, '');
}

export interface LlmRoundNodeRichProps {
  id: string;
  data: LlmRoundData;
}

export const LlmRoundNodeRich: React.FC<LlmRoundNodeRichProps> = ({
  id,
  data,
}) => {
  const { darkMode } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useNodeDimensions(id, wrapperRef);
  const tabTrigger = useTabFocalTrigger(data.turnKey);

  const model = shortenModel(data.modelUsed);
  const runState = data.runState ?? 'completed';
  const paletteKey: string =
    runState === 'running'
      ? 'running'
      : runState === 'error'
      ? 'error'
      : 'ok';
  const palette = getPaletteEntry(paletteKey);

  return (
    <div
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      aria-label={roundAriaLabel(data)}
      onFocus={tabTrigger.onFocus}
      onBlur={tabTrigger.onBlur}
      className={`w-[260px] rounded-lg shadow-md border focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 ${palette.bgClass}`}
      data-node-id={id}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!opacity-0 !pointer-events-none"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="r"
        className="!opacity-0 !pointer-events-none"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="b"
        className="!opacity-0 !pointer-events-none"
      />
      <div
        className={`p-3 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-300'
        }`}
      >
        <div
          className={`font-bold text-sm ${
            darkMode ? 'text-gray-100' : 'text-gray-900'
          } flex items-center gap-2`}
        >
          <span>{data.isSynthesis ? '⚡' : '⚙'}</span>
          <span>{data.isSynthesis ? 'Synthesis' : 'Round'}</span>
          <span
            className={`font-mono font-normal text-xs ${
              darkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            #{data.round}
          </span>
          <span className={`ml-auto ${palette.color}`}>{palette.icon}</span>
        </div>
        <div
          className={`text-[10px] mt-0.5 ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          <span className={palette.color}>
            {palette.label ?? runState}
          </span>
        </div>
      </div>
      <div className="p-3 text-[11px]">
        {model && (
          <div className="mb-2">
            <div
              className={`text-[9px] uppercase tracking-widest mb-0.5 ${
                darkMode ? 'text-gray-500' : 'text-gray-500'
              }`}
            >
              Model
            </div>
            <div
              className={`font-mono ${
                darkMode ? 'text-gray-300' : 'text-gray-800'
              }`}
            >
              {model}
            </div>
          </div>
        )}
        <div
          className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
        >
          {data.toolCallCount > 0
            ? `→ ${data.toolCallCount} ${
                data.toolCallCount === 1 ? 'tool' : 'tools'
              } fanning out`
            : data.isSynthesis
            ? 'writing the final answer'
            : 'no tools this round'}
        </div>
      </div>
    </div>
  );
};
